import React, {
  useEffect,
  createContext,
  useState,
  lazy,
  Suspense,
} from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import NavBar from "./ui/src/NavBar";
import LandingPage from "./ui/src/LandingPage";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuthInstance } from "./ui/src/firebase";
import { IntercomChat } from "./ui/src/components/Chat/IntercomChat";
import ErrorBoundary from "./ui/src/components/ErrorBoundary";
import { DesignerErrorFallback, ViewerErrorFallback, AnalyticsErrorFallback } from "./ui/src/components/ErrorFallbacks";
import { getOnboarding } from "./ui/src/serverComm";
import { 
  setUser, 
  setActiveSurvey, 
  setActiveWorkspace, 
  updateCurrentSubscription, 
  updateTotalResponses, 
  updateAiUses 
} from "./store/appSlice";
import { useDispatch } from "react-redux";
import AuthAction from "./ui/src/pages/AuthAction";
import AuthPage from "./ui/src/pages/AuthPage";
import TermsOfServicePage from "./ui/src/pages/TermsOfServicePage";
import CookiePolicyPage from "./ui/src/pages/CookiePolicyPage";
import DataProcessingAgreement from "./ui/src/pages/DataProcessingAgreement";
import ResponsibleDisclosurePage from "./ui/src/pages/ResponsibleDisclosurePage";
import { AppLoader } from "./ui/src/components/AppLoader";
import SurveyListSkeleton from "./ui/src/components/SurveyListSkeleton";
import NavBarSkeleton from "./ui/src/components/NavBarSkeleton";
import VerifyNewEmail from "./ui/src/pages/VerifyNewEmail";
import NotFoundPage from "./ui/src/pages/NotFoundPage";
import MetaPixel from "./ui/src/components/MetaPixel";

const SurveyDesigner = lazy(() => import("./ui/src/SurveyDesigner"));
const SurveyList = lazy(() => import("./ui/src/SurveyList"));
const SurveyViewer = lazy(() => import("./ui/src/SurveyViewer"));
const SurveyOverview = lazy(() => import("./ui/src/SurveyOverview"));
const SurveyResponses = lazy(() => import("./ui/src/SurveyResponses"));
const SurveySettings = lazy(() => import("./ui/src/SurveySettings"));
const ProfileSettings = lazy(() => import("./ui/src/pages/ProfileSettings"));
const SurveyIntegrate = lazy(() => import("./ui/src/pages/SurveyIntegrate"));
const BrandkitPage = lazy(() => import("./ui/src/pages/BrandkitPage"));
const PlansPage = lazy(() => import("./ui/src/pages/PlansPage"));
const PrivacyPolicyPage = lazy(() => import("./ui/src/pages/PrivacyPolicyPage"));
const NewSurvey = lazy(() => import("./ui/src/NewSurvey"));
const Onboarding = lazy(() => import("./ui/src/pages/Onboarding"));
const GoogleOAuthCallback = lazy(() => import("./ui/src/pages/GoogleOAuthCallback"));
const CreateThemePage = lazy(() => import("./ui/src/pages/CreateThemePage"));

export const UserContext = createContext(null);

// TEMPORARILY SKIP ONBOARDING - set to false to re-enable onboarding flow
const SKIP_ONBOARDING = false;

const SurveyAnalytics = lazy(() => import("./ui/src/SurveyAnalytics"));
const SurveyLogic = lazy(() => import("./ui/src/pages/SurveyLogic"));
const AcceptInvitePage = lazy(() => import("./ui/src/pages/AcceptInvitePage"));

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const auth = getAuthInstance();
  const [user, userLoading, error] = useAuthState(auth);
  const [initializing, setInitializing] = useState(true);
  const [onboardingData, setOnboardingData] = useState(null);

  useEffect(() => {
    if (!userLoading && user && user.emailVerified) {
      // If valid user exists
      dispatch(setUser(user));
      setInitializing(false);
      getOnboarding().then(onboarding => {
        setOnboardingData(onboarding);
      }).catch(() => {});
    } else if (!userLoading && (!user || !user.emailVerified)) {
      // If valid user not exists, clear all Redux state
      dispatch(setUser(null));
      dispatch(setActiveSurvey(null));
      dispatch(setActiveWorkspace(null));
      dispatch(updateCurrentSubscription(null));
      dispatch(updateTotalResponses(0));
      dispatch(updateAiUses(0));
      setOnboardingData(null);
      setInitializing(false);
    }
  }, [user, userLoading, dispatch]);


  // Protected route handler
  const ProtectedRoute = ({ children }) => {
    const isSurveyPath = location.pathname.startsWith("/s/");
    const isOnboardingPath = location.pathname.includes("/onboarding");
    const searchParams = new URLSearchParams(location.search || "");
    const isPreviewMode = isOnboardingPath && (searchParams.get("preview") || "").toLowerCase() === "true";

    // Allow public access to onboarding preview without redirects/guards
    if (isOnboardingPath && isPreviewMode) {
      return children;
    }

    // SKIP ONBOARDING: When enabled, skip the onboarding redirect
    // To re-enable onboarding, set SKIP_ONBOARDING to false at the top of this file
    if (!SKIP_ONBOARDING && onboardingData) {
      if (onboardingData.isCompleted && isOnboardingPath) {
        return <Navigate to="/workspaces" replace />;
      }
      const expectedOnboardingPath = `/onboarding?step=${onboardingData.currentStep}`;
      const fullPath = `${location.pathname}${location.search}`;
      const isAlreadyOnExpectedPath = fullPath === expectedOnboardingPath;
      if (!onboardingData.isCompleted && !isAlreadyOnExpectedPath) {
        return (
          <Navigate
            to={`/onboarding?step=${onboardingData.currentStep}`}
            replace
          />
        );
      }
    }

    if ((!user || !user.emailVerified) && !isSurveyPath) {
      return <Navigate to="/auth" replace />;
    }

    return children;
  };

  const isPublicSurveyPath = location.pathname.includes("/s/");

  if (initializing) {
    if (location.pathname === "/workspaces") {
      // Check if mobile - use AppLoader for mobile, skeleton for desktop
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      if (isMobile) {
        return <AppLoader coverage="full" backgroundColor="#FFFFFF" />;
      }
      return (
        <div className="w-full min-h-screen pt-14">
          <NavBarSkeleton />
          <SurveyListSkeleton viewMode="tiles" />
        </div>
      );
    }
    return <AppLoader coverage="full" backgroundColor="#FFFFFF" />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }


  const isOnboardingRoute = location.pathname === "/onboarding";

  // Route-aware Suspense fallback so the lazy-chunk loading state matches the
  // destination and stays consistent across in-app navigation:
  //  - Public survey pages: branded full-screen loader (no NavBar there).
  //  - Workspaces: keep its skeleton (the real NavBar is already mounted).
  //  - Every other in-app page (Create/Logic/Settings/Results, etc.): a loader
  //    that sits BELOW the persistent NavBar, vertically centered. This keeps
  //    the NavBar visible and the spinner in the same spot between tab switches,
  //    instead of a full-screen overlay that covers the bar in one place and a
  //    top-aligned spinner in another.
  const isMobileViewport = typeof window !== "undefined" && window.innerWidth < 768;
  const appPageFallback = (
    <div className="fixed inset-0 bg-white" style={{ paddingTop: "var(--navbar-height, 64px)" }}>
      <div className="flex justify-center items-center h-full">
        <AppLoader coverage="partial" />
      </div>
    </div>
  );
  const suspenseFallback = location.pathname.startsWith("/s/") ? (
    <AppLoader coverage="full" backgroundColor="#FFFFFF" />
  ) : location.pathname === "/workspaces" && !isMobileViewport ? (
    <SurveyListSkeleton viewMode="tiles" />
  ) : (
    appPageFallback
  );

  return (
    <div className={`w-full min-h-screen ${isOnboardingRoute ? '' : 'pt-14'}`}>
      <MetaPixel />
      <UserContext.Provider value={user}>
        <div className="min-h-screen bg-white">
          {user &&
            user.emailVerified &&
            !window.location.pathname.startsWith("/s/") &&
            !isOnboardingRoute && (
              <NavBar />
            )}
          <main className="w-full ">
            <ErrorBoundary>
            <Suspense fallback={suspenseFallback}>
            <Routes>
              {/* Survey filling routes  */}
              <Route path="/s/:surveyId" element={
                <ErrorBoundary fallback={ViewerErrorFallback}>
                  <SurveyViewer />
                </ErrorBoundary>
              } />

              {/* Auth route */}
              <Route path="/auth" element={<AuthPage />} />

              {/* Landing page - redirects based on auth status */}
              <Route
                path="/"
                element={
                  user && user.emailVerified ? (
                    <Navigate to="/workspaces" replace />
                  ) : (
                    <Navigate to="/auth" replace />
                  )
                }
              />


              {/* Protected routes */}

              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/workspaces"
                element={
                  <ProtectedRoute>
                    <SurveyList />
                  </ProtectedRoute>
                }
              />
              {/* Backward compat: redirect old /surveys to /workspaces */}
              <Route path="/surveys" element={<Navigate to="/workspaces" replace />} />
              <Route
                path="/surveys/new"
                element={
                  <ProtectedRoute>
                    <NewSurvey />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/survey/:surveyId"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary fallback={DesignerErrorFallback}>
                      <SurveyDesigner />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/survey/:surveyId/analytics"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary fallback={AnalyticsErrorFallback}>
                      <Suspense fallback={appPageFallback}>
                        <SurveyAnalytics
                          surveyId={window.location.pathname.split("/")[2]}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route path="/survey/:id/overview" element={<SurveyOverview />} />
              <Route
                path="/survey/:id/responses"
                element={<SurveyResponses />}
              />
              <Route path="/survey/:id/settings" element={<SurveySettings />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brandkit"
                element={
                  <ProtectedRoute>
                    <BrandkitPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brandkit/:brandkitId"
                element={
                  <ProtectedRoute>
                    <BrandkitPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brandkit/:brandkitId/themes"
                element={
                  <ProtectedRoute>
                    <BrandkitPage />
                  </ProtectedRoute>
                }
              />
          <Route
            path="/brandkit/:brandkitId/themes/create"
            element={
              <ProtectedRoute>
                <CreateThemePage />
              </ProtectedRoute>
            }
          />
              <Route
                path="/survey/:surveyId/integrate"
                element={<SurveyIntegrate />}
              />
              <Route
                path="/survey/:surveyId/logic"
                element={
                  <Suspense fallback={appPageFallback}>
                    <SurveyLogic />
                  </Suspense>
                }
              />
              <Route
                path="/google-oauth/callback"
                element={<GoogleOAuthCallback />}
              />
              <Route path="/auth-action" element={<AuthAction />} />
              <Route path="/verify-new-email" element={<VerifyNewEmail />} />
              <Route
                path="/invite/:token"
                element={
                  <Suspense fallback={appPageFallback}>
                    <AcceptInvitePage />
                  </Suspense>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </main>
          {!isPublicSurveyPath && !location.pathname.includes("/onboarding") && (
            <IntercomChat user={user} />
          )}
        </div>
      </UserContext.Provider>
    </div>
  );
}

export default App;
